import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, Star } from "lucide-react";

export default function Victories() {
  const t = useT();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ["victories"],
    queryFn: async () => (await api.get("/victories")).data,
    retry: false,
  });

  const items = Array.isArray(q.data) ? q.data : q.data?.items || [];

  const add = async () => {
    const val = text.trim();
    if (!val) return;
    setBusy(true);
    try {
      await api.post("/victories", { text: val, title: val });
      setText("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["victories"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.victories")}
        title={t("client.victoryToday")}
        subtitle={t("brand.tagline")}
      />

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex gap-2">
        <Input
          data-testid="victory-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder={t("client.victoryToday")}
          className="rounded-xl bg-cream-50 h-11"
        />
        <Button
          onClick={add}
          data-testid="victory-add"
          disabled={busy}
          className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5"
        >
          <Star className="h-4 w-4 mr-1" /> {t("common.add")}
        </Button>
      </div>

      {q.isLoading && <LoadingCard height="h-24" />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={Trophy} title={t("common.empty")} description={t("client.victoryToday")} testId="victories-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="victories-list">
        {items.map((v, i) => (
          <div key={v.id || i} className="rounded-2xl bg-white shadow-card p-4 hover:shadow-card-hover transition">
            <div className="text-sage-600 mb-1"><Trophy className="h-4 w-4" strokeWidth={1.5} /></div>
            <div className="text-ink-900">{v.text || v.title || v.note || "Victory"}</div>
            {v.created_at && <div className="text-xs text-ink-500 mt-1">{new Date(v.created_at).toLocaleDateString()}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
